// src/pages/MyOrders.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { CartContext } from '../context/CartContext';
import { NotificationContext } from '../context/NotificationContext';
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';
import { Role } from '../utils/enums';
import InquiryCard from '../components/inquiries/InquiryCard'; // Import InquiryCard
import './MyOrders.css';

const MyOrders = () => {
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, updateUser } = useContext(UserContext);
  const { fetchCart } = useContext(CartContext);
  const { addNotification } = useContext(NotificationContext);
  const [isDownloading, setIsDownloading] = useState(null); // Store the ID of the order being downloaded
  const [dotCount, setDotCount] = useState(0); // New state for animating dots
  const [isCancelling, setIsCancelling] = useState(null); // New state for cancellation
  const [selectedInquiry, setSelectedInquiry] = useState(null); // State for the inquiry modal
  const [fetchingInquiryId, setFetchingInquiryId] = useState(null); // Loading state for inquiry fetch
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pollingIntervals = useRef({}); // Use a ref to store polling interval IDs

  // Use a ref to hold the latest fetchCart function to avoid adding it to the polling effect's dependencies.
  const fetchCartRef = useRef(fetchCart);
  useEffect(() => {
    fetchCartRef.current = fetchCart;
  });

  const view = searchParams.get('view');
  const isAdminView = view === 'all' && user.isAdmin;

  const handleViewInquiryClick = async (inquiryId) => {
    if (!inquiryId) {
      addNotification('Inquiry ID is missing.', 'error');
      return;
    }
    setFetchingInquiryId(inquiryId);
    try {
      // We assume an endpoint exists to fetch a single inquiry by its ID.
      const response = await apiFetch(`${BASE_URL}/api/inquiries/inquiry/${inquiryId}`, {}, user.token);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch inquiry details.');
      }
      const inquiryData = await response.json();
      setSelectedInquiry(inquiryData);
    } catch (error) {
      addNotification(error.message, 'error');
      setSelectedInquiry(null);
    } finally {
      setFetchingInquiryId(null);
    }
  };

  // Effect to handle payment cancellations when redirected back to this page
  useEffect(() => {
    const orderId = searchParams.get('orderId');
    const isCancelled = searchParams.get('cancelled') === 'true';

    if (isCancelled && orderId) {
      const handleCancellation = async () => {
        try {
          const response = await apiFetch(`${BASE_URL}/api/payments/cancel`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ orderId }) }, user.token);
          const result = await response.json();
          if (result.user) {
            updateUser(result.user);
          }
          addNotification('Payment was cancelled and any held credits have been refunded.', 'info');
        } catch (error) {
          addNotification(`Error cancelling order: ${error.message}`, 'error');
        } finally {
          // Clean the URL to prevent re-triggering on refresh
          searchParams.delete('cancelled');
          searchParams.delete('orderId');
          setSearchParams(searchParams, { replace: true });
        }
      };
      handleCancellation();
    }
  }, [searchParams, setSearchParams, user.token, addNotification, updateUser]);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        // Determine the scope for the API call based on the URL query parameter
        const scope = isAdminView ? 'all' : 'my';
        const response = await apiFetch(`${BASE_URL}/api/orders?scope=${scope}`, {}, user.token);

        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setOrders(data);
          } else {
            throw new Error('Received invalid data format for orders.');
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to fetch orders.');
        }
      } catch (error) {
        addNotification(error.message || 'Failed to fetch orders.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (user.isLoggedIn) {
      fetchOrders();
    }
  }, [user.isLoggedIn, user.token, addNotification, location.search, isAdminView]);

  // New effect for polling processing orders
  useEffect(() => {
    const pollableStatuses = ['processing', 'authorized', 'payment-initiated', 'authorizing'];
    const terminalStatuses = ['completed', 'failed', 'generation-failed', 'payment-failed', 'payment-cancelled', 'payment-timeout', 'action-required', 'refunded'];

    const checkOrderStatus = async (orderId) => {
      try {
        const response = await apiFetch(`${BASE_URL}/api/orders/${orderId}/status`, {}, user.token);
        if (response.ok) {
          // Update dot animation on each successful poll
          setDotCount((prevCount) => (prevCount + 1) % 5);

          const { order: updatedOrderData, user: updatedUserData } = await response.json();

          // Replace the old order data with the fresh data from the server
          setOrders((currentOrders) => currentOrders.map((o) => (o._id === orderId ? updatedOrderData : o)));

          // If the backend sent back updated user info (e.g., on order completion), update the context.
          if (updatedUserData) {
            updateUser({ creditPoints: updatedUserData.creditPoints, pointsOnHold: updatedUserData.pointsOnHold });
          }

          if (terminalStatuses.includes(updatedOrderData.status)) {
            clearInterval(pollingIntervals.current[orderId]);
            delete pollingIntervals.current[orderId];
            if (updatedOrderData.status === 'completed') {
              addNotification(`Order #${orderId.slice(-8)} is now complete!`, 'success');
              // Refresh the cart since the backend clears it upon order completion.
              fetchCartRef.current();
            }
          }
        }
      } catch (error) {
        console.error(`Error polling for order ${orderId}:`, error);
        // Optionally stop polling on error
        clearInterval(pollingIntervals.current[orderId]);
        delete pollingIntervals.current[orderId];
      }
    };

    orders.forEach((order) => {
      if (pollableStatuses.includes(order.status) && !pollingIntervals.current[order._id]) {
        console.log(`Starting to poll for order: ${order._id}`);
        pollingIntervals.current[order._id] = setInterval(() => checkOrderStatus(order._id), 5000); // Poll every 5 seconds
      }
    });

    // Cleanup function to clear all intervals when the component unmounts or orders change
    return () => {
      Object.values(pollingIntervals.current).forEach(clearInterval);
      pollingIntervals.current = {};
    };
    // We only want to re-run this effect when the `orders` array itself changes,
    // not on every re-render. The user.token is needed for the API call inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, user.token]);

  const isReportDownloadable = (order) => {
    // This logic is now per-item, but we can check the overall order date for a general check
    const orderDate = new Date(order.createdAt);
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    return orderDate >= fourteenDaysAgo;
  };

  const handleDownloadClick = async (orderId, propertyId) => {
    // This function now takes orderId and propertyId
    if (!orderId || !propertyId) return;

    const order = orders.find((o) => o._id === orderId);
    if (!isReportDownloadable(order) && user.role !== Role.Admin) {
      addNotification('This report download link has expired (older than 14 days).', 'info');
      return;
    }

    setIsDownloading(`${orderId}-${propertyId}`); // Use a unique key for downloading state
    addNotification('Preparing your report for download...', 'info');

    try {
      const token = user.token;
      if (!token) {
        addNotification('You must be logged in to download reports.', 'error');
        setIsDownloading(null);
        return;
      }
      // The download URL now includes the propertyId
      const response = await apiFetch(`${BASE_URL}/api/orders/${orderId}/download/${propertyId}`, {}, token);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;

        const contentDisposition = response.headers.get('content-disposition');
        let filename = `report_for_property_${propertyId}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch && filenameMatch.length === 2) filename = filenameMatch[1];
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        addNotification('Report download started!', 'success');
      } else {
        const errorData = await response.json();
        addNotification(errorData.message || 'Failed to download report.', 'error');
      }
    } catch (error) {
      addNotification('An error occurred while downloading the report.', 'error');
    } finally {
      setIsDownloading(null);
    }
  };

  const handleCancelOrder = async (orderId) => {
    setIsCancelling(orderId);
    try {
      // Use the 'abandon' endpoint to delete the order and refund credits.
      const response = await apiFetch(`${BASE_URL}/api/orders/${orderId}/abandon`, { method: 'DELETE' }, user.token);
      const data = await response.json();

      if (response.ok) {
        addNotification('Order cancelled successfully.', 'success');
        // Remove the cancelled order from the local state.
        setOrders((prevOrders) => prevOrders.filter((order) => order._id !== orderId));
        if (data.newCreditBalance !== undefined) {
          // Update the user's credit balance in the UI.
          updateUser({ creditPoints: data.newCreditBalance });
        }
      } else {
        throw new Error(data.message || 'Failed to cancel order.');
      }
    } catch (error) {
      addNotification(error.message, 'error');
    } finally {
      setIsCancelling(null);
    }
  };

  if (isLoading) {
    return <div className="my-orders-container">Loading your orders...</div>;
  }

  // Filter out orders that users shouldn't see (e.g., system-cancelled).
  // Admins should see all orders, so we only filter for the regular user view.
  const visibleOrders = isAdminView ? orders : orders.filter((order) => !['payment-cancelled', 'payment-timeout'].includes(order.status));

  if (visibleOrders.length === 0) {
    return <div className="my-orders-container">You have no orders yet.</div>;
  }

  const getPaymentMethodLabel = (provider) => {
    if (provider === 'paypal') {
      return '(PayPal)';
    }
    if (provider === 'rivhit') {
      return '(Card)';
    }
    // Fallback for older orders or other cases where provider is not set
    return '(Card)';
  };

  return (
    <div className="my-orders-container">
      <h2 className="my-orders-title">{isAdminView ? t('myOrders.titleAdmin') : t('myOrders.titleUser')}</h2>
      <div className="orders-list">
        {visibleOrders.map((order) => {
          const isCancellable = order.status === 'pending';
          const isOrderDownloadable = isReportDownloadable(order) || user.role === Role.Admin;

          return (
            <div key={order._id} className="order-row">
              {/* Left Column: Order Summary */}
              <div className="order-summary-column">
                <div className="order-summary-header">
                  <span className="order-summary-date">{new Date(order.createdAt).toLocaleString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  <span className={`order-summary-status status-${order.status.toLowerCase()}`}>{order.status}</span>
                </div>
                <span className="order-summary-id">{t('myOrders.orderIdLabel', { orderId: order._id.slice(-8) })}</span>
                <div className="order-summary-cost">
                  {order.amountPaidWithCard > 0 && (
                    <span className="cost-card">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: order.currency || 'ILS' }).format(order.amountPaidWithCard)} {getPaymentMethodLabel(order.paymentProvider)}
                    </span>
                  )}
                  {order.pointsSpent > 0 && <span className="cost-credits">{t('myOrders.credits', { count: order.pointsSpent })}</span>}
                  {order.isRefunded && <span className="refunded-tag">{t('myOrders.refunded')}</span>}
                </div>
                {isCancellable && (
                  <button className="cancel-order-btn" onClick={() => handleCancelOrder(order._id)} disabled={isCancelling === order._id}>
                    {isCancelling === order._id ? t('myOrders.cancelling') : t('myOrders.cancelOrder')}
                  </button>
                )}
              </div>

              {/* Right Column: Order Items */}
              <div className="order-items-column">
                {order.items.map((item, index) => {
                  const itemIsProcessing = item.status?.startsWith('retrieving') || ['drafting-report', 'generating-report', 'generating-pdf'].includes(item.status);
                  return (
                    <div key={index} className="order-item-detail">
                      <div className="item-name-status-wrapper">
                        <span className="item-name">{item.name}</span>
                        {itemIsProcessing && (
                          <span className={`item-status-badge status-${item.status.toLowerCase()}`}>
                            {item.status.startsWith('retrieving') ? `${item.status.replace(':', ' ')}${'.'.repeat(dotCount)}` : t(`myOrders.itemStatus.${item.status}`, { dots: '.'.repeat(dotCount) })}
                          </span>
                        )}
                      </div>
                      <div className="item-actions">
                        <span className="item-cost">{t('myOrders.credits', { count: item.costInCredits })}</span>
                        {item.productType === 'report' && item.reportFilename && (
                          <button
                            className="download-btn"
                            onClick={() => handleDownloadClick(order._id, item.propertyId)}
                            disabled={isDownloading === `${order._id}-${item.propertyId}` || order.status !== 'completed'}
                            title={order.status === 'completed' ? (isOrderDownloadable ? t('myOrders.downloadReportTitle') : t('myOrders.downloadExpiredTitle')) : t('myOrders.downloadNotReadyTitle')}
                          >
                            {isDownloading === `${order._id}-${item.propertyId}` ? t('myOrders.downloading') : t('myOrders.download')}
                          </button>
                        )}
                        {item.productType === 'inquiry' && order.status === 'completed' && (
                          <button className="view-inquiry-btn" onClick={() => handleViewInquiryClick(item.inquiryId)} disabled={fetchingInquiryId === item.inquiryId}>
                            {fetchingInquiryId === item.inquiryId ? t('myOrders.viewingInquiry') : t('myOrders.viewInquiry')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Handle legacy orders without an items array */}
                {!order.items && (
                  <div className="order-item-detail">
                    <span className="item-name">{t('myOrders.legacyOrder')}</span>
                  </div>
                )}
                {order.notes && (
                  <div className="order-notes">
                    <strong>{t('myOrders.notes')}:</strong> {order.notes}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Inquiry Details Modal */}
      {selectedInquiry && (
        <div className="inquiry-modal-overlay" onClick={() => setSelectedInquiry(null)}>
          <div className="inquiry-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="inquiry-modal-header">
              <h3>{t('myOrders.inquiryDetailsTitle')}</h3>
              <button onClick={() => setSelectedInquiry(null)} className="inquiry-modal-close-btn">
                &times;
              </button>
            </div>
            <div className="inquiry-modal-body">
              <InquiryCard inquiry={selectedInquiry} forceExpanded={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
